import { Outlet, Navigate } from "@tanstack/react-router";
import { isClientType } from "@amsaas/shared/types";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useActiveUser } from "../../hooks/useActiveUser";
import { getAppHost, audienceAllowedOnHost } from "../../lib/appHost";
import { AppSidebar } from "./app-sidebar";
import { SiteHeader } from "./site-header";

/** Shell for all /provider/* routes — shadcn-admin SidebarProvider/Inset pattern. */
export function ProviderLayout(): JSX.Element {
  const {
    isLoading,
    isError,
    isSignedIn,
    userType,
    appAudience,
    platformName,
  } = useActiveUser();

  // Defence-in-depth only — the API enforces all permissions (05-frontend-design §9).
  // ENH-004: userType is the ACTIVE tenant's role (auth.me), not global Clerk metadata.
  if (isLoading)
    return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!isSignedIn || isError) return <Navigate to="/login" />;
  if (userType && isClientType(userType))
    return <Navigate to="/portal/dashboard" />;
  // Host-as-trust-boundary (AUTH-06/08, GAP-025). Providers resolve to 'both' so the
  // host-agnostic console (GAP-001) passes on either host; this guards edge cases.
  if (appAudience && !audienceAllowedOnHost(appAudience, getAppHost())) {
    return <Navigate to="/" />;
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader platformName={platformName} />
        <div className="flex flex-1 flex-col gap-4 p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
